import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';

import useGetObjectVuls from 'hooks/ObjectVuls/useGetObjectVuls';
import { Vulnerability as ApiResponse } from 'api/vulnerabilities/type';
import useScreenSize from 'hooks/Screen/useScreenSize';
import { useGetObjectVulsColumns } from 'hooks/ObjectVuls/useGetObjectVulsColumns';
import AdvancedFilterDataTable from 'components/FilterDataTable/AdvancedFilterDataTable';
import ObjectVulsExpandable from 'components/ObjectVuls/ObjectVulsExpandable';
import ViewModal from 'components/ViewModal/ViewModal';
import CompleteVulModal from 'pages/InteractiveMap/components/Modals/CompleteVulModal/CompleteVulModal';

const Vulnerabilities: React.FC = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [ordering, setQueryParam] = useState('');
  const [search, setSearch] = useState('');
  const [advancedFilter, setAdvancedFilter] = useState('default');
  const { data, isLoading, refetch } = useGetObjectVuls(slug || '', true, advancedFilter);
  const columns = useGetObjectVulsColumns(slug || '');
  const { isNotDesktop } = useScreenSize();
  const searchFields = ['id', 'floor_plan_name', 'floor_plan_slug', 'criticality', 'category'];

  // Get connected_to_enc parameter from URL
  const connectedToEncParam = searchParams.get('connected_to_enc');
  const encounterIdParam = searchParams.get('encounter_id');

  // Function to clear the connected_to_enc filter
  const clearFilter = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('connected_to_enc');
    setSearchParams(newSearchParams);
  };

  // Filter and sort data based on connected_to_enc parameter
  const filteredAndSortedData = useMemo(() => {
    if (!data || !connectedToEncParam) {
      return data || [];
    }

    // Parse the connected_to_enc parameter (supports both comma-separated and array format)
    let connectedIds: number[] = [];

    if (connectedToEncParam.startsWith('[') && connectedToEncParam.endsWith(']')) {
      // Handle array format: [43,39]
      try {
        connectedIds = JSON.parse(connectedToEncParam);
      } catch (e) {
        console.error('Failed to parse connected_to_enc array:', e);
      }
    } else {
      // Handle comma-separated format: 39,43
      connectedIds = connectedToEncParam
        .split(',')
        .map(id => parseInt(id.trim()))
        .filter(id => !isNaN(id));
    }

    if (connectedIds.length === 0) {
      return data;
    }

    // Filter vulnerabilities by connected IDs and sort by ID
    return data.filter(vul => connectedIds.includes(vul.id)).sort((a, b) => a.id - b.id);
  }, [data, connectedToEncParam]);

  useEffect(() => {
    refetch();
  }, [page, ordering, search, refetch]);

  return (
    <div className="bg-white">
      {/* Show filter info and clear button if connected_to_enc is used */}
      {connectedToEncParam && (
        <div className="bg-white card-header p-3 border-bottom bg-light d-flex justify-content-between align-items-center">
          <h4 className="m-0">Showing vulnerabilities associated with pest sighting: {encounterIdParam}</h4>
          <button type="button" className="btn btn-sm btn-ghost-purple" onClick={clearFilter}>
            Show all PCCs
          </button>
        </div>
      )}
      <AdvancedFilterDataTable<ApiResponse>
        columns={columns}
        data={filteredAndSortedData}
        onChangePage={setPage}
        onChangeFilterParams={setQueryParam}
        amountPerPageLabel="Vulnerabilities Per Page"
        onChangeSearch={setSearch}
        searchFields={searchFields}
        isLoading={isLoading}
        expandableRows={isNotDesktop}
        expandableRowsComponent={ObjectVulsExpandable}
        onChangeAdvancedFilter={setAdvancedFilter}
      />
      <ViewModal />
      <CompleteVulModal refetchVulnerabilities={refetch} />
    </div>
  );
};

export default Vulnerabilities;
